import { useMemo, type ReactElement } from "react";
import { cx } from "@/utils/cx";

/**
 * DiffViewer — line-by-line diff dvou consent textů (cs/en variants).
 *
 * Princip: deterministic per-line classification (added / removed / unchanged).
 * Color-not-only signaling (REQ-UI-018 + WCAG AA 1.4.1): symbol prefixes
 * `+` / `-` / ` ` + aria-label, ne jenom barva.
 *
 * Dark mode parita: barvy přes UUI utility tokeny (bg-utility-green/red/gray)
 * — automaticky se přepínají v .dark. Kontrast 4.5:1 ověřuje axe-core test.
 *
 * Algorithm: jednoduchý LCS (Longest Common Subsequence) per řádek — pro malé
 * consent texty (≤ 1000 řádků) je O(n²) komplexnost přijatelná.
 */

export type DiffLineKind = "added" | "removed" | "unchanged";

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
  oldLineNo?: number;
  newLineNo?: number;
}

export interface DiffViewerProps {
  /** Předchozí verze textu (např. "2026-01-01"). */
  previous: string;
  /** Nová verze textu (např. "2026-05-13"). */
  current: string;
  /** Volitelný aria-label kontejneru. */
  label?: string;
}

export function computeDiff(previous: string, current: string): DiffLine[] {
  const a = previous.split(/\r?\n/);
  const b = current.split(/\r?\n/);
  const m = a.length;
  const n = b.length;
  // DP table for LCS lengths.
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (a[i] === b[j]) {
        dp[i]![j] = (dp[i + 1]?.[j + 1] ?? 0) + 1;
      } else {
        dp[i]![j] = Math.max(dp[i + 1]?.[j] ?? 0, dp[i]?.[j + 1] ?? 0);
      }
    }
  }
  const result: DiffLine[] = [];
  let i = 0;
  let j = 0;
  let oldLine = 1;
  let newLine = 1;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      result.push({ kind: "unchanged", text: a[i]!, oldLineNo: oldLine++, newLineNo: newLine++ });
      i++;
      j++;
    } else if ((dp[i + 1]?.[j] ?? 0) >= (dp[i]?.[j + 1] ?? 0)) {
      result.push({ kind: "removed", text: a[i]!, oldLineNo: oldLine++ });
      i++;
    } else {
      result.push({ kind: "added", text: b[j]!, newLineNo: newLine++ });
      j++;
    }
  }
  while (i < m) {
    result.push({ kind: "removed", text: a[i++]!, oldLineNo: oldLine++ });
  }
  while (j < n) {
    result.push({ kind: "added", text: b[j++]!, newLineNo: newLine++ });
  }
  return result;
}

const PREFIX: Record<DiffLineKind, string> = {
  added: "+",
  removed: "-",
  unchanged: " ",
};

const ARIA: Record<DiffLineKind, string> = {
  added: "přidaný řádek",
  removed: "odstraněný řádek",
  unchanged: "nezměněný řádek",
};

const LINE_CLASSES: Record<DiffLineKind, string> = {
  added: "bg-utility-green-50 text-utility-green-700",
  removed: "bg-utility-red-50 text-utility-red-700",
  unchanged: "bg-secondary text-secondary",
};

const STAT_CLASSES: Record<"added" | "removed", string> = {
  added: "text-utility-green-700",
  removed: "text-utility-red-700",
};

export function DiffViewer({ previous, current, label }: DiffViewerProps): ReactElement {
  const lines = useMemo(() => computeDiff(previous, current), [previous, current]);
  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const l of lines) {
      if (l.kind === "added") added++;
      else if (l.kind === "removed") removed++;
    }
    return { added, removed };
  }, [lines]);

  return (
    <div
      className="overflow-hidden rounded-xl ring-1 ring-secondary"
      role="region"
      aria-label={label ?? "Porovnání verzí consent textu"}
    >
      <div
        className="flex flex-wrap gap-4 border-b border-secondary bg-secondary px-3 py-2 text-sm font-medium"
        aria-live="polite"
      >
        <span className={STAT_CLASSES.added}>+{stats.added} řádků přidáno</span>
        <span className={STAT_CLASSES.removed}>−{stats.removed} řádků odstraněno</span>
      </div>
      <ol className="m-0 list-none p-0 font-mono text-xs" aria-label="Seznam řádků">
        {lines.map((line, idx) => (
          <li
            key={`${line.kind}-${idx}`}
            className={cx("flex items-start gap-2 px-3 py-0.5", LINE_CLASSES[line.kind])}
            data-kind={line.kind}
            aria-label={ARIA[line.kind]}
          >
            <span className="w-3 shrink-0 text-center select-none" aria-hidden="true">
              {PREFIX[line.kind]}
            </span>
            <span className="w-12 shrink-0 text-right opacity-60 select-none" aria-hidden="true">
              {line.oldLineNo ?? "·"}/{line.newLineNo ?? "·"}
            </span>
            <span className="whitespace-pre-wrap break-words">{line.text}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
